export default function KnowledgeHealthPanel({
  healthStats,
  isExpanded,
  setIsExpanded,
  onRunHealthCheck,
  isHealthChecking,
  onRunAiHealthCheck,
  isAuditing,
  setActiveTab,
  setAssetViewMode,
  setIsSyncTrayExpanded,
  onOpenPendingSummaries,
  setSelectedInsightStatus,
  setSelectedInsightKind,
  setSelectedInsightType,
  setSelectedInsightSourceNoteId,
  setSelectedInsightSourceIds,
  setSelectedNoteId,
  setSelectedInsightId,
  setSelectedAssetId,
  setInsightSearchText,
  setNoteSearchText,
  setAssetSearchText,
  setSelectedAssetFilter,
  setSelectedNoteFilter,
}) {
  if (!isExpanded) return null;

  const resetFilters = () => {
    setInsightSearchText('');
    setNoteSearchText('');
    setAssetSearchText('');
    setSelectedInsightSourceNoteId(null);
    setSelectedInsightSourceIds([]);
    setSelectedInsightKind?.('all');
    setSelectedInsightType('all');
    setSelectedNoteId?.(null);
    setSelectedInsightId?.(null);
    setSelectedAssetId?.(null);
    setSelectedAssetFilter?.('all');
    setSelectedNoteFilter?.('all');
  };

  const items = [
    {
      label: 'Noteまとめ待ち',
      count: healthStats.pendingSummaries,
      color: 'text-blue-400',
      action: () => {
        resetFilters();
        setActiveTab('input');
        onOpenPendingSummaries?.();
        setIsExpanded?.(false);
      }
    },
    {
      label: 'レビュー待ちHint',
      count: healthStats.suggestedHints,
      color: 'text-purple-400',
      action: () => {
        resetFilters();
        setAssetViewMode('insights');
        setSelectedInsightStatus('suggested');
        setSelectedInsightKind('hint');
        setSelectedInsightType('all');
        setIsSyncTrayExpanded(true);
        setIsExpanded?.(false);
      }
    },
    {
      label: '更新提案',
      count: healthStats.proposedChanges,
      color: 'text-indigo-400',
      action: () => {
        resetFilters();
        setAssetViewMode('insights');
        setSelectedInsightStatus('suggested');
        setSelectedInsightKind('proposed_change');
        setSelectedInsightType('proposal_actionable');
        setIsSyncTrayExpanded(true);
        setIsExpanded?.(false);
      }
    },
    {
      label: 'Missing Link候補',
      count: healthStats.missingLinks,
      color: 'text-rose-400',
      action: () => {
        resetFilters();
        setAssetViewMode('insights');
        setSelectedInsightStatus('suggested');
        setSelectedInsightType('missing_link');
        setIsSyncTrayExpanded(true);
        setIsExpanded?.(false);
      }
    },
    {
      label: '見直しAsset',
      count: healthStats.reviewAssets,
      color: 'text-amber-400',
      action: () => {
        resetFilters();
        setAssetViewMode('assets');
        setSelectedAssetFilter?.('review');
        setIsSyncTrayExpanded(true);
        setIsExpanded?.(false);
      }
    },
    {
      label: '休眠Asset',
      count: healthStats.dormantAssets,
      color: 'text-slate-400',
      action: () => {
        resetFilters();
        setAssetViewMode('assets');
        setSelectedAssetFilter?.('dormant');
        setIsSyncTrayExpanded(true);
        setIsExpanded?.(false);
      }
    },
    {
      label: 'アーカイブ候補',
      count: healthStats.archiveCandidates,
      color: 'text-rose-400',
      action: () => {
        resetFilters();
        setAssetViewMode('assets');
        setSelectedAssetFilter?.('archive_candidate');
        setIsSyncTrayExpanded(true);
        setIsExpanded?.(false);
      }
    },
  ];

  return (
    <div className="p-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={onRunHealthCheck}
          disabled={isHealthChecking}
          className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-bold transition-all border ${
            isHealthChecking
              ? 'bg-gray-800/50 text-gray-500 border-gray-700 cursor-not-allowed'
              : 'bg-gray-800/60 text-gray-300 border-gray-700 hover:bg-gray-700/60'
          }`}
        >
          <svg className={`w-3.5 h-3.5 shrink-0 ${isHealthChecking ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {isHealthChecking ? '実行中...' : '重複チェック'}
        </button>
        <button
          onClick={onRunAiHealthCheck}
          disabled={isAuditing}
          className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-bold transition-all border ${
            isAuditing
              ? 'bg-gray-800/50 text-gray-500 border-gray-700 cursor-not-allowed'
              : 'bg-indigo-950/40 text-indigo-300 border-indigo-800/50 hover:bg-indigo-900/60 hover:border-indigo-600/60'
          }`}
        >
          <svg className={`w-3.5 h-3.5 shrink-0 ${isAuditing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          {isAuditing ? '実行中...' : 'AIチェック'}
        </button>
      </div>
      <p className="text-[10px] text-gray-500 leading-relaxed">
        AIが情報の矛盾や重複、更新が必要なNote / Assetを検出し、更新案を整理提案として出します。タグ整理候補は低優先で、主要カウントには含めません。
      </p>
      <p className="text-[10px] text-gray-500 leading-relaxed">
        件数はレビュー待ちの目安です。全件を今すぐ処理する必要はなく、迷うものは保留のまま残せます。
      </p>
      <div className="grid grid-cols-2 gap-2">
        {items.map((item, idx) => (
          <button
            key={idx}
            onClick={item.action}
            disabled={item.count === 0}
            className={`flex flex-col p-2 rounded-lg border transition-all text-left ${
              item.count > 0
                ? 'bg-gray-900/50 border-gray-700 hover:border-gray-500'
                : 'bg-gray-900/20 border-gray-800/50 opacity-40'
            }`}
          >
            <span className={`text-[9px] font-bold uppercase tracking-wider mb-1 ${item.color}`}>{item.label}</span>
            <span className="text-lg font-black text-white leading-none">{item.count}</span>
          </button>
        ))}
      </div>
      {healthStats.tagMergeSuggestions > 0 && (
        <button
          onClick={() => {
            resetFilters();
            setAssetViewMode('insights');
            setSelectedInsightStatus('suggested');
            setSelectedInsightKind('proposed_change');
            setSelectedInsightType('tag_merge');
            setIsSyncTrayExpanded(true);
            setIsExpanded?.(false);
          }}
          className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg border border-gray-800 bg-gray-900/30 text-left text-[10px] text-gray-500 hover:text-gray-300 hover:border-gray-700 transition-colors"
        >
          <span>タグ整理候補</span>
          <span className="font-bold">{healthStats.tagMergeSuggestions}</span>
        </button>
      )}
    </div>
  );
}
